import { useEffect, useState } from "react";
import { Link, Navigate, Route, Routes, useLocation } from "react-router-dom";
import HomePage from "./pages/Home";
import ThermoimagingPage from "./pages/Thermoimaging";
import SettingsPage from "./pages/Settings";
import ThicknessPage from "./pages/Thickness";
import ColorPage from "./pages/Color";
import ImpressumPage from "./pages/Impressum";
import DatenschutzPage from "./pages/Datenschutz";
import AnalyticsConsentBanner from "./components/AnalyticsConsentBanner";
import ShowAccessGate from "./components/ShowAccessGate";
import { trackAnalyticsEvent, trackAnalyticsPageview } from "./analytics";
import { isShowMode } from "./showMode";

function Nav() {
  const location = useLocation();
  const [infoMenuOpen, setInfoMenuOpen] = useState(false);

  useEffect(() => {
    setInfoMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!infoMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setInfoMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [infoMenuOpen]);

  const closeInfoMenu = () => setInfoMenuOpen(false);

  return (
    <header className="app-header draggable">
      <div className="app-header-main">
        <div className="app-brand">
          <h1>ZeroCO2 | Process Interface</h1>
        </div>
        <nav className="app-nav no-drag" aria-label="Main navigation">
          <Link to="/" className="pill" aria-current={location.pathname === "/"}>
            Home
          </Link>
          <Link
            to="/thermoimaging"
            className="pill"
            aria-current={location.pathname === "/thermoimaging"}
            onClick={() => trackAnalyticsEvent("nav_thermoimaging_opened")}
          >
            Thermoimaging
          </Link>
          <Link
            to="/thickness"
            className="pill"
            aria-current={location.pathname === "/thickness"}
            onClick={() => trackAnalyticsEvent("nav_thickness_opened")}
          >
            Thickness Plotter
          </Link>
          {!isShowMode && (
            <>
              <Link
                to="/color"
                className="pill"
                aria-current={location.pathname === "/color"}
              >
                Color
              </Link>
              <Link
                to="/settings"
                className="pill"
                aria-current={location.pathname === "/settings"}
              >
                Settings
              </Link>
            </>
          )}
          {isShowMode && (
            <button
              type="button"
              className="pill app-info-toggle no-drag"
              aria-expanded={infoMenuOpen}
              aria-controls="app-info-nav"
              onClick={() => setInfoMenuOpen((v) => !v)}
            >
              More info {infoMenuOpen ? "▴" : "▾"}
            </button>
          )}
        </nav>
      </div>
      {isShowMode && (
        <nav
          id="app-info-nav"
          className={`app-info-nav no-drag${infoMenuOpen ? " is-open" : ""}`}
          aria-label="Public information"
        >
          <Link
            to="/impressum"
            className="pill"
            aria-current={location.pathname === "/impressum"}
            onClick={closeInfoMenu}
          >
            Legal Notice
          </Link>
          <Link
            to="/datenschutz"
            className="pill"
            aria-current={location.pathname === "/datenschutz"}
            onClick={closeInfoMenu}
          >
            Privacy Policy
          </Link>
          <a
            className="pill"
            href="https://www.marvinbay.de"
            target="_blank"
            rel="noreferrer"
            onClick={closeInfoMenu}
          >
            Marvin Bay
          </a>
          <a
            className="pill"
            href="https://www.ipgr.com"
            target="_blank"
            rel="noreferrer"
            onClick={closeInfoMenu}
          >
            IPGR
          </a>
          <a
            className="pill"
            href="https://www.ghi.rwth-aachen.de"
            target="_blank"
            rel="noreferrer"
            onClick={closeInfoMenu}
          >
            GHI - RWTH
          </a>
        </nav>
      )}
    </header>
  );
}

function AnalyticsRouteTracker() {
  const location = useLocation();

  useEffect(() => {
    trackAnalyticsPageview();
  }, [location.pathname, location.search, location.hash]);

  return null;
}

export default function App() {
  return (
    <ShowAccessGate>
      <div className="app-shell">
        <AnalyticsRouteTracker />
        <Nav />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/thermoimaging" element={<ThermoimagingPage />} />
          <Route path="/thickness" element={<ThicknessPage />} />
          <Route path="/color" element={isShowMode ? <Navigate to="/" replace /> : <ColorPage />} />
          <Route
            path="/settings"
            element={isShowMode ? <Navigate to="/" replace /> : <SettingsPage />}
          />
          <Route path="/impressum" element={<ImpressumPage />} />
          <Route path="/datenschutz" element={<DatenschutzPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
      <AnalyticsConsentBanner />
    </ShowAccessGate>
  );
}
